/**
 * NotificationContext - Manages Firebase Cloud Messaging (FCM) for push notifications
 * 
 * Features:
 * - Request notification permission
 * - Get and store FCM token
 * - Listen for foreground messages
 * - Send token to backend
 * - Handle notification clicks
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useAuth } from '../AuthContext/AuthContext'
import { useSnackbar } from 'notistack'
import axios from 'axios'
import { API_URL } from 'config'
import {
  requestNotificationPermission,
  onForegroundMessage,
  showNotification,
  getNotificationPermission
} from 'services/fcm'

const NotificationContext = createContext({})

const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [fcmToken, setFcmToken] = useState(null)
  const [permissionStatus, setPermissionStatus] = useState(getNotificationPermission())
  const [isRequestingPermission, setIsRequestingPermission] = useState(false)

  /**
   * Send FCM token to backend
   */
  const saveFcmTokenToBackend = useCallback(async (token) => {
    if (!token || !user?.id) return

    try {
      await axios.post(`${API_URL}/client/notifications/register-token`, {
        token,
        device_type: 'web',
        user_agent: navigator.userAgent
      })
      console.log('FCM token saved to backend')
    } catch (error) {
      console.error('Failed to save FCM token:', error)
    }
  }, [user?.id])

  /**
   * Request notification permission and get FCM token
   */
  const requestPermission = useCallback(async () => {
    if (isRequestingPermission) return null
    
    setIsRequestingPermission(true)
    try {
      const token = await requestNotificationPermission()
      
      if (token) {
        setFcmToken(token)
        setPermissionStatus('granted')
        
        // Save token to backend
        if (user?.id) {
          await saveFcmTokenToBackend(token)
        }
        
        enqueueSnackbar('Notifications enabled successfully', { variant: 'success' })
        return token
      } else {
        setPermissionStatus(Notification.permission)
        if (Notification.permission === 'denied') {
          enqueueSnackbar('Notifications blocked. Enable in browser settings.', { variant: 'warning' })
        }
        return null
      }
    } catch (error) {
      console.error('Failed to get notification permission:', error)
      enqueueSnackbar('Failed to enable notifications', { variant: 'error' })
      return null
    } finally {
      setIsRequestingPermission(false)
    }
  }, [isRequestingPermission, user?.id, saveFcmTokenToBackend, enqueueSnackbar])

  /**
   * Auto-register FCM token when user logs in (if permission already granted)
   */
  useEffect(() => {
    if (isAuthenticated && user?.id && permissionStatus === 'granted' && !fcmToken) {
      requestPermission()
    }
  }, [isAuthenticated, user?.id, permissionStatus, fcmToken]) // eslint-disable-line

  /**
   * Listen for foreground messages
   */
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground notification received:', payload)
      
      const title = payload.notification?.title || 'New Notification'
      const body = payload.notification?.body || ''
      const url = payload.data?.url || '/'
      
      // Show browser notification
      showNotification(title, {
        body,
        icon: payload.notification?.icon || '/favicon.png',
        data: { url },
        tag: payload.data?.tag || 'default'
      })
      
      // Also show snackbar
      enqueueSnackbar(body || title, {
        variant: 'info',
        autoHideDuration: 5000,
      })
    })

    return unsubscribe
  }, [enqueueSnackbar])

  /**
   * Update permission status periodically
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setPermissionStatus(getNotificationPermission())
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  return (
    <NotificationContext.Provider
      value={{
        fcmToken,
        permissionStatus,
        isRequestingPermission,
        requestPermission,
        isNotificationSupported: 'Notification' in window,
        isNotificationEnabled: permissionStatus === 'granted',
      }}
      displayName="Notification"
    >
      {children}
    </NotificationContext.Provider>
  )
}

const useNotification = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within NotificationProvider')
  }
  return context
}

export { NotificationProvider, useNotification }
